import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Thermometer, SprayCan, FileText, AlertTriangle,
  Loader2, CheckCircle2, LayoutTemplate, Globe,
} from "lucide-react";
import {
  BUSINESS_TYPES, ZONE_LABELS, EQUIPMENT_TYPE_LABELS, FREQUENCY_LABELS,
} from "@/components/templates/BUSINESS_TEMPLATES";

export default function ApplyTemplateModal({ open, onClose, venue }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (open && venue) {
      hosys.entities.BusinessTemplate.filter({ active: true })
        .then(all => {
          const relevant = all.filter(t =>
            t.active &&
            (t.business_type === venue.venue_type ||
             t.business_type === "restaurant") // fallback
          );
          setTemplates(relevant);
          setSelected(relevant[0] || null);
        });
    }
  }, [open, venue]);

  const handleApply = async () => {
    if (!selected || !venue) return;
    setApplying(true);

    // Crear equips de temperatura
    const tempEquip = selected.temperature_equipment || [];
    for (const eq of tempEquip) {
      await hosys.entities.TemperatureRecord.create({
        venue_id: venue.id,
        equipment_name: eq.name,
        equipment_type: eq.equipment_type,
        min_limit: eq.min_limit,
        max_limit: eq.max_limit,
        temperature: 0,
        status: "correct",
        date: new Date().toISOString().split("T")[0],
        observations: "Registre inicial creat des de plantilla",
      });
    }

    // Crear tasques de neteja
    const cleanTasks = selected.cleaning_tasks || [];
    for (const ct of cleanTasks) {
      await hosys.entities.CleaningRecord.create({
        venue_id: venue.id,
        zone: ct.zone,
        task: ct.task,
        frequency: ct.frequency,
        completed: false,
        date: new Date().toISOString().split("T")[0],
        observations: "Tasca creada des de plantilla",
      });
    }

    setApplying(false);
    setApplied(true);
  };

  const handleClose = () => {
    setApplied(false);
    setPreview(false);
    setSelected(null);
    onClose();
  };

  if (applied) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Plantilla aplicada!</h3>
              <p className="text-sm text-gray-500 mt-1">
                S'han creat {(selected?.temperature_equipment || []).length} equips de temperatura i {(selected?.cleaning_tasks || []).length} tasques de neteja per a <strong>{venue?.name}</strong>.
              </p>
            </div>
            <Button onClick={handleClose} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">Tancar</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutTemplate className="w-5 h-5 text-[#7B2D3F]" />
            Aplicar plantilla a {venue?.name}
          </DialogTitle>
        </DialogHeader>

        {templates.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No hi ha plantilles disponibles per al tipus "{BUSINESS_TYPES[venue?.venue_type]?.label || venue?.venue_type}".
            <br />Crea-les des de la secció Plantilles.
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Selecciona una plantilla per configurar automàticament els equips i tasques inicials del local:
            </p>

            {/* Template picker */}
            <div className="space-y-2">
              {templates.map(t => {
                const bt = BUSINESS_TYPES[t.business_type] || { label: t.business_type, emoji: "📋" };
                return (
                  <button
                    key={t.id}
                    onClick={() => { setSelected(t); setPreview(false); }}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                      selected?.id === t.id
                        ? "border-[#7B2D3F] bg-[#7B2D3F]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{bt.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-900">{t.name}</span>
                          {t.is_global && <Badge className="text-[10px] bg-blue-100 text-blue-700 border-0 px-1.5 py-0"><Globe className="w-2.5 h-2.5 mr-0.5 inline" />Global</Badge>}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t.temperature_equipment?.length || 0} equips · {t.cleaning_tasks?.length || 0} tasques neteja
                        </p>
                      </div>
                      {selected?.id === t.id && <CheckCircle2 className="w-5 h-5 text-[#7B2D3F] flex-shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Preview toggle */}
            {selected && (
              <div>
                <button
                  onClick={() => setPreview(!preview)}
                  className="text-xs text-[#7B2D3F] underline"
                >
                  {preview ? "Amagar" : "Veure"} el detall de la plantilla
                </button>
                {preview && (
                  <div className="mt-3 space-y-3">
                    {(selected.temperature_equipment || []).length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1"><Thermometer className="w-3.5 h-3.5" /> Equips de temperatura</p>
                        <div className="space-y-1">
                          {selected.temperature_equipment.map((eq, i) => (
                            <div key={`entry-${i}`} className="flex justify-between text-xs bg-blue-50 rounded px-2 py-1.5">
                              <span>{eq.name} <span className="text-gray-400">({EQUIPMENT_TYPE_LABELS[eq.equipment_type] || eq.equipment_type})</span></span>
                              <span className="text-blue-700 font-mono">{eq.min_limit}° – {eq.max_limit}°C</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(selected.cleaning_tasks || []).length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1"><SprayCan className="w-3.5 h-3.5" /> Tasques de neteja</p>
                        <div className="space-y-1">
                          {selected.cleaning_tasks.map((ct, i) => (
                            <div key={`ct-${i}`} className="flex justify-between text-xs bg-green-50 rounded px-2 py-1.5">
                              <span>{ct.task}</span>
                              <div className="flex gap-1">
                                <Badge variant="outline" className="text-[10px] px-1 py-0">{ZONE_LABELS[ct.zone] || ct.zone}</Badge>
                                <Badge className="text-[10px] px-1 py-0 bg-green-100 text-green-700 border-0">{FREQUENCY_LABELS[ct.frequency] || ct.frequency}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel·lar</Button>
          <Button
            onClick={handleApply}
            disabled={!selected || applying}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F]"
          >
            {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Aplicant...</> : "Aplicar plantilla"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}